import React, { useState, useEffect, useRef } from "react";
import { NAVY, GOLD, BEIGE, SLATE, WHITE, S } from "../constants";

const EMOJIS = ["😊","👍","🎉","✅","📧","📞","🏌️","⭐","🤝","💼","📅","🔔","💡","🏆","👋","🙏","📝","💰","🎯","✨"];

function RichToolbar({ editorRef }) {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const savedSelectionRef = useRef(null);
  const colorInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const exec = (cmd, value = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, value);
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
  };

  const restoreSelection = () => {
    const sel = window.getSelection();
    if (savedSelectionRef.current && sel) {
      sel.removeAllRanges();
      sel.addRange(savedSelectionRef.current);
    }
  };

  const insertLink = () => {
    restoreSelection();
    if (linkUrl) {
      const display = linkText || linkUrl;
      exec("insertHTML", `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${display}</a>`);
    }
    setShowLinkInput(false);
    setLinkUrl("");
    setLinkText("");
  };

  const insertEmoji = (emoji) => {
    editorRef.current?.focus();
    exec("insertText", emoji);
    setShowEmojiPicker(false);
  };

  const Btn = ({ cmd, value, title, children, onClick }) => (
    <button type="button" title={title}
      onMouseDown={e => { e.preventDefault(); onClick ? onClick() : exec(cmd, value); }}
      style={{ padding: "3px 7px", border: "1px solid #ddd", borderRadius: 4, background: "#fff", cursor: "pointer", fontSize: 12, color: NAVY, minWidth: 28, height: 26, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {children}
    </button>
  );

  const Divider = () => <div style={{ width: 1, height: 18, background: "#ddd", margin: "0 2px" }} />;

  return (
    <div style={{ borderBottom: "1px solid #eee", background: "#fafafa" }}>
      <div style={{ display: "flex", gap: 3, padding: "6px 12px", flexWrap: "wrap", alignItems: "center" }}>
        {/* Font family */}
        <select
          onMouseDown={() => { const sel = window.getSelection(); if (sel && sel.rangeCount > 0) savedSelectionRef.current = sel.getRangeAt(0).cloneRange(); }}
          onChange={e => { restoreSelection(); exec("fontName", e.target.value); editorRef.current?.focus(); }}
          style={{ fontSize: 11, border: "1px solid #ddd", borderRadius: 4, padding: "2px 4px", color: NAVY, height: 26, cursor: "pointer" }}>
          <option value="Arial">Arial</option>
          <option value="Georgia">Georgia</option>
          <option value="Times New Roman">Times New Roman</option>
          <option value="Courier New">Courier New</option>
          <option value="Verdana">Verdana</option>
          <option value="Merriweather">Merriweather</option>
          <option value="Open Sans">Open Sans</option>
        </select>

        {/* Font size */}
        <select
          onMouseDown={() => { const sel = window.getSelection(); if (sel && sel.rangeCount > 0) savedSelectionRef.current = sel.getRangeAt(0).cloneRange(); }}
          onChange={e => { restoreSelection(); exec("fontSize", e.target.value); editorRef.current?.focus(); }}
          style={{ fontSize: 11, border: "1px solid #ddd", borderRadius: 4, padding: "2px 4px", color: NAVY, height: 26, cursor: "pointer" }}>
          <option value="1">10</option>
          <option value="2">12</option>
          <option value="3">14</option>
          <option value="4">16</option>
          <option value="5">18</option>
          <option value="6">24</option>
          <option value="7">32</option>
        </select>

        <Divider />
        <Btn cmd="bold" title="Bold"><b>B</b></Btn>
        <Btn cmd="italic" title="Italic"><i>I</i></Btn>
        <Btn cmd="underline" title="Underline"><u>U</u></Btn>

        <Divider />

        {/* Text color */}
        <button type="button" title="Text color"
          onMouseDown={e => { e.preventDefault(); saveSelection(); colorInputRef.current?.click(); }}
          style={{ padding: "3px 7px", border: "1px solid #ddd", borderRadius: 4, background: "#fff", cursor: "pointer", fontSize: 12, height: 26, display: "flex", alignItems: "center", gap: 3 }}>
          A <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: "#e53935" }} />
        </button>
        <input ref={colorInputRef} type="color" defaultValue="#e53935"
          onChange={e => { restoreSelection(); exec("foreColor", e.target.value); }}
          style={{ width: 0, height: 0, opacity: 0, position: "absolute" }} />

        <Divider />
        <Btn cmd="justifyLeft" title="Align left">≡</Btn>
        <Btn cmd="justifyCenter" title="Center">≡</Btn>
        <Btn cmd="justifyRight" title="Align right">≡</Btn>

        <Divider />
        <Btn cmd="insertUnorderedList" title="Bullet list">• ≡</Btn>
        <Btn cmd="insertOrderedList" title="Numbered list">1. ≡</Btn>

        <Divider />
        <Btn cmd="outdent" title="Outdent">⇤</Btn>
        <Btn cmd="indent" title="Indent">⇥</Btn>

        <Divider />

        {/* Link */}
        <Btn title="Insert link" onClick={() => { saveSelection(); setShowLinkInput(s => !s); }}>🔗</Btn>

        {/* Emoji */}
        <div style={{ position: "relative" }}>
          <Btn title="Emoji" onClick={() => { saveSelection(); setShowEmojiPicker(s => !s); }}>😊</Btn>
          {showEmojiPicker && (
            <div style={{ position: "absolute", top: 30, left: 0, background: "#fff", border: "1px solid #ddd", borderRadius: 8, padding: 6, zIndex: 200, display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 3, boxShadow: "0 4px 16px rgba(0,0,0,0.15)" }}>
              {EMOJIS.map(em => (
                <button key={em} type="button" onMouseDown={e => { e.preventDefault(); insertEmoji(em); }}
                  style={{ background: "none", border: "none", fontSize: 16, cursor: "pointer", padding: 3, borderRadius: 4 }}>{em}</button>
              ))}
            </div>
          )}
        </div>

        {/* Attach */}
        <Btn title="Attach file" onClick={() => fileInputRef.current?.click()}>📎</Btn>
        <input ref={fileInputRef} type="file" multiple style={{ display: "none" }}
          onChange={e => {
            e.target.value = "";
          }} />
      </div>

      {/* Link input row */}
      {showLinkInput && (
        <div style={{ display: "flex", gap: 6, padding: "6px 12px", borderTop: "1px solid #eee", alignItems: "center" }}>
          <input style={{ flex: 1, border: "1px solid #ddd", borderRadius: 4, padding: "4px 8px", fontSize: 12, outline: "none" }}
            placeholder="Display text" value={linkText} onChange={e => setLinkText(e.target.value)} />
          <input style={{ flex: 2, border: "1px solid #ddd", borderRadius: 4, padding: "4px 8px", fontSize: 12, outline: "none" }}
            placeholder="https://..." value={linkUrl} onChange={e => setLinkUrl(e.target.value)} />
          <button type="button" onMouseDown={e => { e.preventDefault(); insertLink(); }}
            style={{ background: NAVY, color: "#fff", border: "none", borderRadius: 4, padding: "4px 12px", cursor: "pointer", fontSize: 12 }}>Add</button>
          <button type="button" onMouseDown={e => { e.preventDefault(); setShowLinkInput(false); }}
            style={{ background: "none", border: "1px solid #ddd", borderRadius: 4, padding: "4px 10px", cursor: "pointer", fontSize: 12 }}>✕</button>
        </div>
      )}
    </div>
  );
}

export function GmailInbox({ currentUser, signature = "" }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [emailBody, setEmailBody] = useState(null);
  const [search, setSearch] = useState("");
  const searchInputRef = useRef(null);
  const [error, setError] = useState("");
  const [showReply, setShowReply] = useState(false);
  const [replySending, setReplySending] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [composeTo, setComposeTo] = useState("");
  const [composeCc, setComposeCc] = useState("");
  const [composeBcc, setComposeBcc] = useState("");
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [composeSubject, setComposeSubject] = useState("");
  const [composeSending, setComposeSending] = useState(false);
  const composeBodyRef = useRef(null);
  const replyBodyRef = useRef(null);
  const prevShowCompose = useRef(false);
  const prevShowReply = useRef(false);

  const sigToHtml = (sig) => sig
    ? `<br><br>-- <br>${String(sig).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br>")}`
    : "";

  const fetchInbox = async (query = "in:inbox") => {
    setLoading(true);
    setError("");
    const userId = window._supabaseAuthId;
    const rawToken = typeof localStorage !== "undefined" ? localStorage.getItem("gmail-access-token") : null;
    const token = (!rawToken || rawToken === "null") ? null : rawToken;
    if (!userId || !token) { setError("Gmail not connected. Please connect Gmail in Settings."); setLoading(false); return; }
    const resp = await fetch("/api/gmail-inbox", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId, query, maxResults: 25, token })
    });
    const data = await resp.json();
    if (!resp.ok) {
      if (resp.status === 401 || data.error?.includes("invalid authentication") || data.error?.includes("Invalid Credentials")) {
        setLoading(false);
        localStorage.removeItem("gmail-access-token");
        sessionStorage.setItem("postOAuthRedirect", "inbox");
        const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
        const redirectUri = window.location.origin;
        const scope = encodeURIComponent("https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/gmail.send");
        const state = encodeURIComponent(JSON.stringify({ action: "gmail_sync" }));
        window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=token&scope=${scope}&state=${state}&prompt=none`;
        return;
      }
      setError(data.error || "Failed to load inbox");
      setLoading(false);
      return;
    }
    setMessages(data.messages || []);
    setLoading(false);
  };

  useEffect(() => { fetchInbox(); }, []);

  if (showCompose && !prevShowCompose.current) {
    queueMicrotask(() => {
      if (composeBodyRef.current) composeBodyRef.current.innerHTML = sigToHtml(signature) || "<p><br></p>";
    });
  }
  prevShowCompose.current = showCompose;

  if (showReply && !prevShowReply.current) {
    queueMicrotask(() => {
      if (replyBodyRef.current) replyBodyRef.current.innerHTML = sigToHtml(signature) || "<p><br></p>";
    });
  }
  prevShowReply.current = showReply;

  const filtered = messages.filter(m =>
    !search || m.from?.toLowerCase().includes(search.toLowerCase()) ||
    m.subject?.toLowerCase().includes(search.toLowerCase()) ||
    m.snippet?.toLowerCase().includes(search.toLowerCase())
  );

  const getToken = () => {
    const t = typeof localStorage !== "undefined" ? localStorage.getItem("gmail-access-token") : null;
    return t && t !== "null" ? t : null;
  };

  const handleSelectEmail = async (msg) => {
    setSelected(msg);
    setShowReply(false);
    setEmailBody(null);
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch("/api/gmail-inbox", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "getMessage", messageId: msg.id, token, user_id: window._supabaseAuthId })
      });
      const data = await res.json();
      if (data.success) setEmailBody(data.body);
    } catch (e) {
      console.error("Failed to fetch full email body", e);
    }
  };

  const sendReply = async () => {
    const body = replyBodyRef.current?.innerHTML || "";
    if (!body.trim() || !selected) return;
    setReplySending(true);
    const userId = window._supabaseAuthId;
    const resp = await fetch("/api/gmail-send", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: userId,
        to: selected.from,
        subject: selected.subject?.startsWith("Re:") ? selected.subject : `Re: ${selected.subject}`,
        body,
        token: getToken()
      })
    });
    const data = await resp.json();
    if (resp.ok) {
      setShowReply(false);
      setSelected(null);
      alert("Reply sent!");
    } else {
      alert(data.error || "Failed to send reply");
    }
    setReplySending(false);
  };

  const sendCompose = async () => {
    const body = composeBodyRef.current?.innerHTML || "";
    if (!composeTo || !composeSubject || !body.trim()) return;
    setComposeSending(true);
    const userId = window._supabaseAuthId;
    const resp = await fetch("/api/gmail-send", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: userId,
        to: composeTo,
        cc: composeCc || undefined,
        bcc: composeBcc || undefined,
        subject: composeSubject,
        body,
        token: getToken()
      })
    });
    const data = await resp.json();
    if (resp.ok) {
      setShowCompose(false);
      setComposeTo(""); setComposeCc(""); setComposeBcc(""); setComposeSubject("");
      alert("Email sent!");
    } else {
      alert(data.error || "Failed to send");
    }
    setComposeSending(false);
  };

  return (
    <div style={{ display: "flex", height: "calc(100vh - 120px)", gap: 0, background: BEIGE, borderRadius: 12, overflow: "hidden", border: "1px solid #E8E3D8" }}>
      {/* Left panel */}
      <div style={{ width: 380, borderRight: "1px solid #E8E3D8", display: "flex", flexDirection: "column", background: WHITE }}>
        <div style={{ padding: "16px", borderBottom: "1px solid #E8E3D8" }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <div style={{ position: "relative", flex: 1 }}>
              <input
                ref={searchInputRef}
                style={{ ...S.input, flex: 1, fontSize: 13, paddingRight: 28 }}
                placeholder="Search emails..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === "Enter" && fetchInbox(search || "in:inbox")}
              />
              {search.length > 0 && (
                <button
                  type="button"
                  onClick={() => { setSearch(""); requestAnimationFrame(() => searchInputRef.current?.focus()); }}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#5B6770",
                    fontSize: "14px",
                  }}
                >
                  ×
                </button>
              )}
            </div>
            <button type="button" onClick={() => fetchInbox(search || "in:inbox")} style={{ background: NAVY, color: WHITE, border: "none", borderRadius: 8, padding: "0 14px", cursor: "pointer", fontSize: 13 }}>
              🔄
            </button>
            <button type="button" onClick={() => { setShowCompose(true); }} style={{ background: NAVY, color: WHITE, border: "none", borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
              ✏️ Compose
            </button>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {["in:inbox", "is:unread", "in:sent"].map(q => (
              <button key={q} type="button" onClick={() => fetchInbox(q)} style={{ fontSize: 11, padding: "4px 10px", borderRadius: 20, border: "1px solid #ddd", background: BEIGE, cursor: "pointer", color: NAVY }}>
                {q === "in:inbox" ? "Inbox" : q === "is:unread" ? "Unread" : "Sent"}
              </button>
            ))}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto" }}>
          {loading && <div style={{ padding: 24, textAlign: "center", color: SLATE }}>Loading...</div>}
          {error && <div style={{ padding: 24, color: "#e74c3c", fontSize: 13 }}>{error}</div>}
          {!loading && filtered.length === 0 && <div style={{ padding: 24, color: SLATE, fontSize: 13 }}>No messages found.</div>}
          {filtered.map(msg => (
            <div key={msg.id} onClick={() => handleSelectEmail(msg)}
              style={{ padding: "14px 16px", borderBottom: "1px solid #f0ede6", cursor: "pointer", background: selected?.id === msg.id ? BEIGE : WHITE, borderLeft: msg.unread ? `3px solid ${GOLD}` : "3px solid transparent" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <div style={{ fontSize: 13, fontWeight: msg.unread ? 700 : 500, color: NAVY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>
                  {msg.from?.split("<")[0].trim() || msg.from}
                </div>
                <div style={{ fontSize: 11, color: SLATE, whiteSpace: "nowrap", marginLeft: 8 }}>
                  {msg.date ? new Date(msg.date).toLocaleDateString() : ""}
                </div>
              </div>
              <div style={{ fontSize: 12, fontWeight: msg.unread ? 600 : 400, color: "#333", marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {msg.subject || "(no subject)"}
              </div>
              <div style={{ fontSize: 11, color: SLATE, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {msg.snippet}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel */}
      <div style={{ flex: 1, padding: 24, overflowY: "auto" }}>
        {!selected && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: SLATE, fontSize: 14 }}>
            Select an email to read
          </div>
        )}
        {selected && (
          <div>
            <h2 style={{ fontFamily: "Merriweather, serif", color: NAVY, marginBottom: 16, fontSize: 18 }}>{selected.subject || "(no subject)"}</h2>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20, padding: "12px 0", borderBottom: "1px solid #E8E3D8" }}>
              <div>
                <div style={{ fontSize: 13, color: NAVY, fontWeight: 600 }}>From: {selected.from}</div>
                <div style={{ fontSize: 12, color: SLATE, marginTop: 2 }}>To: {selected.to}</div>
              </div>
              <div style={{ fontSize: 12, color: SLATE }}>{selected.date ? new Date(selected.date).toLocaleString() : ""}</div>
            </div>
            <div style={{ fontSize: 14, color: "#333", lineHeight: 1.6, fontFamily: "Open Sans, sans-serif" }}>
              {emailBody ? (
                emailBody.trim().startsWith("<") ? (
                  <div style={{ fontSize: 14, lineHeight: 1.6, overflowY: "auto" }} dangerouslySetInnerHTML={{ __html: emailBody }} />
                ) : (
                  <div style={{ fontSize: 14, lineHeight: 1.6, overflowY: "auto", whiteSpace: "pre-wrap" }}>{emailBody}</div>
                )
              ) : (
                <div style={{ fontSize: 14, color: SLATE }}>{selected.snippet || "No content available."}</div>
              )}
            </div>

            {/* Reply */}
            <div style={{ marginTop: 24, borderTop: "1px solid #E8E3D8", paddingTop: 16 }}>
              {!showReply ? (
                <button type="button" onClick={() => setShowReply(true)} style={{ background: NAVY, color: "#fff", border: "none", borderRadius: 8, padding: "8px 20px", cursor: "pointer", fontSize: 13 }}>
                  ↩ Reply
                </button>
              ) : (
                <div>
                  <div style={{ fontSize: 12, color: SLATE, marginBottom: 8 }}>
                    Replying to: <strong>{selected.from}</strong>
                  </div>
                  <RichToolbar editorRef={replyBodyRef} />
                  <div
                    ref={replyBodyRef}
                    contentEditable
                    suppressContentEditableWarning
                    data-placeholder="Write your reply..."
                    style={{ width: "100%", minHeight: 140, padding: 12, border: "1px solid #ddd", borderTop: "none", borderRadius: "0 0 8px 8px", fontSize: 13, fontFamily: "Open Sans, sans-serif", lineHeight: 1.6, outline: "none" }}
                  />
                  <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                    <button type="button" onClick={sendReply} disabled={replySending} style={{ background: NAVY, color: "#fff", border: "none", borderRadius: 8, padding: "8px 20px", cursor: "pointer", fontSize: 13 }}>
                      {replySending ? "Sending..." : "Send Reply"}
                    </button>
                    <button type="button" onClick={() => { setShowReply(false); }} style={{ background: "none", border: "1px solid #ddd", borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13 }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Compose modal */}
      {showCompose && (
        <div style={{ position: "fixed", bottom: 24, right: 32, width: 600, background: WHITE, borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.2)", zIndex: 1000, display: "flex", flexDirection: "column", overflow: "hidden", maxHeight: "80vh" }}>
          {/* Header */}
          <div style={{ background: NAVY, color: WHITE, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>New Message</span>
            <button type="button" onClick={() => setShowCompose(false)} style={{ background: "none", border: "none", color: WHITE, cursor: "pointer", fontSize: 18, lineHeight: 1 }}>×</button>
          </div>

          {/* To / Cc / Bcc / Subject */}
          <div style={{ borderBottom: "1px solid #eee" }}>
            <div style={{ display: "flex", alignItems: "center", padding: "8px 16px", borderBottom: "1px solid #f0f0f0" }}>
              <span style={{ fontSize: 12, color: SLATE, width: 44, flexShrink: 0 }}>To</span>
              <input style={{ flex: 1, border: "none", outline: "none", fontSize: 13 }} placeholder="recipient@email.com" value={composeTo} onChange={e => setComposeTo(e.target.value)} />
              <button type="button" onClick={() => setShowCcBcc(s => !s)} style={{ background: "none", border: "none", fontSize: 11, color: SLATE, cursor: "pointer" }}>
                {showCcBcc ? "Hide" : "Cc Bcc"}
              </button>
            </div>
            {showCcBcc && (
              <>
                <div style={{ display: "flex", alignItems: "center", padding: "8px 16px", borderBottom: "1px solid #f0f0f0" }}>
                  <span style={{ fontSize: 12, color: SLATE, width: 44, flexShrink: 0 }}>Cc</span>
                  <input style={{ flex: 1, border: "none", outline: "none", fontSize: 13 }} placeholder="cc@email.com" value={composeCc} onChange={e => setComposeCc(e.target.value)} />
                </div>
                <div style={{ display: "flex", alignItems: "center", padding: "8px 16px", borderBottom: "1px solid #f0f0f0" }}>
                  <span style={{ fontSize: 12, color: SLATE, width: 44, flexShrink: 0 }}>Bcc</span>
                  <input style={{ flex: 1, border: "none", outline: "none", fontSize: 13 }} placeholder="bcc@email.com" value={composeBcc} onChange={e => setComposeBcc(e.target.value)} />
                </div>
              </>
            )}
            <div style={{ display: "flex", alignItems: "center", padding: "8px 16px" }}>
              <span style={{ fontSize: 12, color: SLATE, width: 44, flexShrink: 0 }}>Subject</span>
              <input style={{ flex: 1, border: "none", outline: "none", fontSize: 13 }} placeholder="Subject" value={composeSubject} onChange={e => setComposeSubject(e.target.value)} />
            </div>
          </div>

          {/* Rich toolbar */}
          <RichToolbar editorRef={composeBodyRef} />

          {/* Editor */}
          <div
            ref={composeBodyRef}
            contentEditable
            suppressContentEditableWarning
            style={{ flex: 1, minHeight: 220, padding: "12px 16px", outline: "none", fontSize: 13, fontFamily: "Open Sans, sans-serif", lineHeight: 1.6, overflowY: "auto" }}
          />

          {/* Footer */}
          <div style={{ padding: "10px 16px", borderTop: "1px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fafafa" }}>
            <button type="button" onClick={sendCompose} disabled={composeSending} style={{ background: NAVY, color: WHITE, border: "none", borderRadius: 8, padding: "8px 20px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
              {composeSending ? "Sending..." : "Send"}
            </button>
            <button type="button" onClick={() => { setShowCompose(false); setComposeTo(""); setComposeCc(""); setComposeBcc(""); setComposeSubject(""); }}
              style={{ background: "none", border: "none", color: SLATE, cursor: "pointer", fontSize: 20 }}>🗑</button>
          </div>
        </div>
      )}
    </div>
  );
}
